import { useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as services from "../../services/saleng";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function Stock() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStock = async () => {
      try {
        const [productsResponse, transactionsResponse] = await Promise.all([
          services.getProducts(),
          services.getBranchTransaction(),
        ]);

        const products = productsResponse.data?.data || [];
        const transactions = transactionsResponse.data?.data || [];
        const productsById = new Map(products.map((product) => [product.id, product]));
        const stockByProduct = new Map();

        transactions.forEach((transaction) => {
          const multiplier = transaction.transactionType === "SELL" ? -1 : 1;
          (transaction.items || []).forEach((item) => {
            const current = stockByProduct.get(item.productId) || {
              productId: item.productId,
              productName: item.productName,
              weight: 0,
              total: 0,
            };

            current.weight += (Number(item.weight) || 0) * multiplier;
            current.total += (Number(item.total) || 0) * multiplier;
            stockByProduct.set(item.productId, current);
          });
        });

        setRows(
          Array.from(stockByProduct.values()).map((item, index) => {
            const product = productsById.get(item.productId);
            return {
              ...item,
              id: index + 1,
              productName: product?.title || item.productName || "ไม่ระบุสินค้า",
              category: product?.category || "ไม่ระบุหมวดหมู่",
              averagePrice: item.weight > 0 ? item.total / item.weight : 0,
            };
          })
        );
      } catch (error) {
        console.error("Unable to load stock:", error);
      } finally {
        setLoading(false);
      }
    };

    loadStock();
  }, []);

  const totalWeight = useMemo(
    () => rows.reduce((sum, item) => sum + item.weight, 0),
    [rows]
  );

  const columns = [
    { field: "id", headerName: "ลำดับ", width: 80, align: "center", headerAlign: "center" },
    { field: "productName", headerName: "สินค้า", flex: 2, minWidth: 180 },
    { field: "category", headerName: "หมวดหมู่", flex: 1, minWidth: 140 },
    {
      field: "weight",
      headerName: "คงเหลือ (กก.)",
      flex: 1,
      minWidth: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "averagePrice",
      headerName: "ราคาเฉลี่ย/กก.",
      flex: 1,
      minWidth: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        คลังสินค้า
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        น้ำหนักคงเหลือรวม {formatNumber(totalWeight)} กก.
      </Typography>
      <Paper sx={{ height: "70vh" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          slots={{ noRowsOverlay: () => <Typography sx={{ p: 3 }}>ยังไม่มีรายการสินค้าในคลัง</Typography> }}
        />
      </Paper>
    </Box>
  );
}
